// The CANONICAL document types attached to a completed visit. These are the
// values the review / approval / seal layer keys on
// (`getRequiredDocumentsForVisitType`, `getDocumentReviews`, `maybeSealVisit`)
// and the values stored in `visit_document_approvals.document_type`.
//
// IMPORTANT: this is NOT always what `generated_pdfs.document_type` stores. The
// dominant PDF-generation path persists the PDF field-map names instead
// ("patient_assessment", "cms485", "supervisory"), which diverge from the
// canonical "visit_report" / "cms_485" / "supervisory_evaluation" below. Any
// code that bridges a `generated_pdfs.document_type` (or a correction's
// document type) into the review/approval layer MUST run it through
// `normalizeToDocumentType` first — see that function for the why.
export const DOCUMENT_TYPES = [
    "visit_report",
    "plan_of_care",
    "cms_485",
    "emergency_kardex",
    "supervisory_evaluation",
    "performance_evaluation",
    "welcome_package",
    // The special-visit deliverable (light = the sole PDF; heavy = the addendum
    // alongside the clinical docs). It is canonical AND stored verbatim in
    // `generated_pdfs.document_type`, so it needs no alias below — but it MUST be
    // in this list so the review/approval/seal layer treats it as an approvable,
    // sealable document. Special visits never generate a `visit_report`, so their
    // required-doc set is derived from their actual PDFs (see `requiredDocsForVisit`).
    "special_form"
];
// `generated_pdfs.document_type` and `correction_submissions.document_type`
// historically use the PDF field-map vocabulary, which diverges from the
// canonical `DocumentType` for three docs. The review/approval/seal layer
// keys on the canonical names, so the agency-portal Approve button (which
// posts the stored `generated_pdfs.document_type`) was silently filing
// approvals under names like "cms485" that `maybeSealVisit` never matched —
// the visit could never seal. Normalize at every boundary where a stored
// document type crosses into the review layer.
const PDF_DOCUMENT_TYPE_ALIASES = {
    patient_assessment: "visit_report",
    cms485: "cms_485",
    supervisory: "supervisory_evaluation",
    performance_evaluation: "performance_evaluation"
};
/**
 * Map a stored `document_type` (from `generated_pdfs` or `correction_submissions`,
 * which may use the PDF field-map vocabulary) to its canonical `DocumentType`.
 * Returns the value unchanged when it is already canonical, and `null` for
 * document types that are not part of the review set (e.g. "scheduled_visit",
 * "consent_form", "care_management_referral"). Note `special_form` IS canonical
 * (see DOCUMENT_TYPES) — it maps to itself, not null.
 */
export function normalizeToDocumentType(stored) {
    if (!stored) {
        return null;
    }
    const aliased = PDF_DOCUMENT_TYPE_ALIASES[stored];
    if (aliased) {
        return aliased;
    }
    return DOCUMENT_TYPES.includes(stored) ? stored : null;
}
export const DOCUMENT_LABELS = {
    visit_report: "Visit Report",
    plan_of_care: "Plan of Care",
    cms_485: "CMS-485",
    emergency_kardex: "Emergency Kardex",
    supervisory_evaluation: "Supervisory Evaluation",
    performance_evaluation: "Annual Field and Administrative Performance Evaluation",
    welcome_package: "Welcome Package",
    special_form: "Special Form"
};
// Display order for the documents table and SMS body. Plan of Care comes
// after Welcome Package so the SMS reads "Welcome → POC → Kardex".
export const DOCUMENT_ORDER = [
    "visit_report",
    "welcome_package",
    "plan_of_care",
    "cms_485",
    "emergency_kardex",
    "supervisory_evaluation",
    "performance_evaluation",
    // The special-visit addendum sorts last; on a light special visit it is the
    // only document, on a heavy one it trails the clinical docs.
    "special_form"
];
// Em dash used for shift suffixes. Centralised so PDF / mobile / agency
// portal / admin / SMS all render the exact same separator.
const SHIFT_SEPARATOR = " – ";
/**
 * Format a Plan of Care label, honouring split-shift only when the visit
 * actually has two POCs.
 *
 *   totalPocsOnVisit <= 1  → "Plan of Care"             (ignore shiftLabel)
 *   totalPocsOnVisit  > 1  → "Plan of Care – AM" / "Plan of Care – PM"
 *
 * Why the count matters: `shift_label` lives on `visit_type_phases` per row,
 * so a single-shift visit type could be edited to carry an 'AM' label by
 * accident. Driving the suffix off the per-visit POC count is the only
 * signal that doesn't lie.
 *
 * Use this for every POC-document/section label across mobile, PDF title,
 * PDF filename, agency portal, admin, and SMS body so the wording stays
 * consistent everywhere.
 */
export function formatPocLabel(opts) {
    const base = opts.baseName ?? DOCUMENT_LABELS.plan_of_care;
    if (opts.totalPocsOnVisit <= 1 || !opts.shiftLabel) {
        return base;
    }
    return `${base}${SHIFT_SEPARATOR}${opts.shiftLabel}`;
}
// Format a non-POC document name with its shift label when present.
// Use `formatPocLabel` for plan-of-care — it also accepts the visit's POC
// count so single-shift POCs never get a phantom suffix even if a stale
// shiftLabel sneaks through.
export function formatDocumentName(documentType, shiftLabel) {
    const base = DOCUMENT_LABELS[documentType] ?? documentType;
    return shiftLabel ? `${base}${SHIFT_SEPARATOR}${shiftLabel}` : base;
}
//# sourceMappingURL=documents.js.map